import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Plus, Pencil, Trash2, KeyRound, UserCheck, UserX, ChevronDown } from "lucide-react";
import { toast } from "sonner";

function emptyForm() {
  return {
    nome: "", cognome: "", email: "", username: "", password: "",
    role: "collaboratore", stato: "attivo",
    pos_aziendale: false, conto_agenzia: false, postale_agenzia: false, abbuoni_abilitati: false,
    flusso_cassa_abilitato: false, chiusura_autonoma: false, conto_separato: false,
    sospesi_enabled: false,
    vers_agenzia_abilitato: true, vers_cc_abilitato: true,
    direct_methods: [],
    versamento_mode: "polizze",
    // POS split override per-collab: "" = eredita da agenzia; altrimenti "none" | "separato" | "duplicato"
    pos_split_mode: "",
    pos_methods: null,
  };
}

export default function Collaboratori() {
  const [users, setUsers] = useState([]);
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({}); // errori inline per campo (es. {email: "..."})
  const [advancedOpen, setAdvancedOpen] = useState(false); // "Opzioni avanzate" collassabile

  const load = async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([api.get("/users"), api.get("/agency")]);
      setUsers(r.data);
      setAgency(a.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setErrors({});
    setAdvancedOpen(false);
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ ...emptyForm(), ...u, password: "" });
    setErrors({});
    // in edit apriamo le avanzate perché l'utente probabilmente vuole rivedere qualcosa
    setAdvancedOpen(true);
    setOpen(true);
  };

  // Aggiorna un campo del form e pulisce l'errore inline correlato
  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => (e[name] ? { ...e, [name]: undefined } : e));
  };

  // Mappa gli errori del backend (Pydantic tradotti) nel dizionario `errors`
  const applyBackendErrors = (err) => {
    const data = err?.response?.data;
    if (data?.errors && Array.isArray(data.errors)) {
      const map = {};
      for (const e of data.errors) {
        const loc = e.loc || [];
        const field = [...loc].reverse().find((x) => typeof x === "string" && x !== "body");
        if (field && !map[field]) map[field] = e.msg;
      }
      if (Object.keys(map).length > 0) {
        setErrors(map);
        return true;
      }
    }
    return false;
  };

  const save = async () => {
    setErrors({});
    try {
      // Normalizza pos_split_mode: "" (eredita da agenzia) → null sul backend
      const buildPayload = (base) => {
        const p = { ...base };
        if (p.pos_split_mode === "" || p.pos_split_mode === undefined) {
          p.pos_split_mode = null;
          p.pos_methods = null;
        }
        return p;
      };
      if (editing) {
        const payload = buildPayload(form);
        if (!payload.password) delete payload.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Collaboratore aggiornato");
      } else {
        if (!form.password) {
          setErrors({ password: "La password è obbligatoria per un nuovo utente." });
          return;
        }
        await api.post("/users", buildPayload(form));
        toast.success("Collaboratore creato");
      }
      setOpen(false);
      load();
    } catch (err) {
      const handled = applyBackendErrors(err);
      // Se l'errore è di validazione con campi identificati, apre le avanzate solo
      // se qualcuno dei campi errati è al loro interno.
      if (handled) {
        const advancedFields = new Set([
          "pos_aziendale", "conto_agenzia", "postale_agenzia", "abbuoni_abilitati",
          "flusso_cassa_abilitato", "sospesi_enabled", "chiusura_autonoma",
          "conto_separato", "direct_methods", "versamento_mode", "pos_split_mode",
          "pos_methods", "stato",
        ]);
        const errFields = err?.response?.data?.errors?.map((e) => {
          const loc = e.loc || [];
          return [...loc].reverse().find((x) => typeof x === "string" && x !== "body");
        }) || [];
        if (errFields.some((f) => advancedFields.has(f))) setAdvancedOpen(true);
      }
      toast.error(formatApiError(err));
    }
  };

  const toggleStato = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { stato: u.stato === "attivo" ? "disattivo" : "attivo" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminare l'utente ${u.nome} ${u.cognome}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Utente eliminato");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resetPwd = async (u) => {
    const pwd = window.prompt(`Nuova password per ${u.username}:`);
    if (!pwd) return;
    try {
      await api.put(`/users/${u.id}`, { password: pwd });
      toast.success("Password aggiornata");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Gestione</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Utenze</h1>
          <p className="text-sm text-slate-500 mt-1">Crea, modifica e disattiva collaboratori e utenze interne dell&apos;agenzia.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-user-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuova utenza
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">{editing ? "Modifica utenza" : "Nuova utenza"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              {/* Tipo utenza */}
              <div className="col-span-2 border border-slate-200 rounded-lg p-3 bg-slate-50/60">
                <Label className="text-slate-700 font-semibold">Tipo utenza</Label>
                {editing?.role === "agente" ? (
                  <div className="mt-2 text-sm text-slate-700">Agente — non modificabile</div>
                ) : (
                  <RadioGroup
                    value={form.role}
                    onValueChange={(v) => setForm({ ...form, role: v })}
                    className="mt-2 grid grid-cols-1 gap-2"
                    data-testid="form-role"
                  >
                    <label className={`flex items-start gap-2 p-2 rounded-md border ${form.role === "collaboratore" ? "border-slate-900 bg-white" : "border-slate-200 bg-white"} cursor-pointer`}>
                      <RadioGroupItem value="collaboratore" id="role-collaboratore" data-testid="role-collaboratore" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-slate-900">Collaboratore</div>
                        <div className="text-xs text-slate-500">Ha un proprio foglio cassa giornaliero e può effettuare versamenti.</div>
                      </div>
                    </label>
                    <label className={`flex items-start gap-2 p-2 rounded-md border ${form.role === "interno" ? "border-slate-900 bg-white" : "border-slate-200 bg-white"} cursor-pointer`}>
                      <RadioGroupItem value="interno" id="role-interno" data-testid="role-interno" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-slate-900">Utenza interna</div>
                        <div className="text-xs text-slate-500">Accede solo al foglio cassa di agenzia (lo stesso dell&apos;agente) per registrare gli incassi. Niente report, niente impostazioni, niente versamenti.</div>
                      </div>
                    </label>
                  </RadioGroup>
                )}
              </div>
              <div>
                <Label>Nome</Label>
                <Input data-testid="form-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
              </div>
              <div>
                <Label>Cognome</Label>
                <Input data-testid="form-cognome" value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  data-testid="form-email"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  className={errors.email ? "border-red-400 focus-visible:ring-red-300" : ""}
                  aria-invalid={!!errors.email}
                />
                {errors.email && (
                  <p className="text-xs text-red-600 mt-1" data-testid="form-email-error">{errors.email}</p>
                )}
              </div>
              <div>
                <Label>Username</Label>
                <Input
                  data-testid="form-username"
                  value={form.username}
                  onChange={(e) => setField("username", e.target.value)}
                  className={errors.username ? "border-red-400 focus-visible:ring-red-300" : ""}
                  aria-invalid={!!errors.username}
                />
                {errors.username && (
                  <p className="text-xs text-red-600 mt-1">{errors.username}</p>
                )}
              </div>
              <div>
                <Label>{editing ? "Nuova password (vuoto = invariata)" : "Password"}</Label>
                <Input
                  type="password"
                  data-testid="form-password"
                  value={form.password}
                  onChange={(e) => setField("password", e.target.value)}
                  className={errors.password ? "border-red-400 focus-visible:ring-red-300" : ""}
                  aria-invalid={!!errors.password}
                />
                {errors.password && (
                  <p className="text-xs text-red-600 mt-1">{errors.password}</p>
                )}
              </div>
              <div className="col-span-2 flex items-center justify-between mt-2">
                <div>
                  <Label className="text-slate-700">Attivo</Label>
                  <div className="text-xs text-slate-500">Disattivando, l&apos;utente non potrà più accedere.</div>
                </div>
                <Switch
                  data-testid="form-stato"
                  checked={form.stato === "attivo"}
                  onCheckedChange={(v) => setField("stato", v ? "attivo" : "disattivo")}
                />
              </div>

              {/* ===== OPZIONI AVANZATE (collassabile) ===== */}
              <div className="col-span-2 mt-2 border-t border-slate-200 pt-3">
                <button
                  type="button"
                  onClick={() => setAdvancedOpen((v) => !v)}
                  className="w-full flex items-center justify-between text-left rounded-md px-2 py-2 hover:bg-slate-50"
                  aria-expanded={advancedOpen}
                  data-testid="toggle-advanced-options"
                >
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Opzioni avanzate</div>
                    <div className="text-xs text-slate-500">Puoi modificarle in qualsiasi momento dalla scheda del collaboratore.</div>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${advancedOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                </button>
              </div>

              {advancedOpen && (<>
              {form.role === "collaboratore" && (
                <div className="col-span-2 mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="form-vers-destinazioni">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Modalità di versamento consentite</div>
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-700">Versa in agenzia</Label>
                    <Switch data-testid="form-vers-agenzia" checked={form.vers_agenzia_abilitato !== false} onCheckedChange={(v) => setField("vers_agenzia_abilitato", v)} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-700">Versa su C/C agenzia</Label>
                    <Switch data-testid="form-vers-cc" checked={form.vers_cc_abilitato !== false} onCheckedChange={(v) => setField("vers_cc_abilitato", v)} />
                  </div>
                  <div className="text-xs text-slate-500">Se ne lasci attiva una sola, il collaboratore vedrà solo quella. Non vale per il flusso CC separato con gestione liquidità attiva.</div>
                  {form.vers_agenzia_abilitato === false && form.vers_cc_abilitato === false && (
                    <div className="text-xs text-red-700" data-testid="form-vers-none-warning">Con entrambe disattivate il collaboratore non potrà creare versamenti.</div>
                  )}
                </div>
              )}
              <div className="col-span-2 flex items-center justify-between mt-2">
                <div>
                  <Label className="text-slate-700">POS aziendale</Label>
                  <div className="text-xs text-slate-500">Se attivo, i Bancomat del collaboratore vanno direttamente sul conto agenzia e sono esclusi dal versamento.</div>
                </div>
                <Switch
                  data-testid="form-pos-aziendale"
                  checked={!!form.pos_aziendale}
                  onCheckedChange={(v) => setField("pos_aziendale", v)}
                  disabled={form.role === "interno"}
                />
              </div>
              <div className="col-span-2 flex items-center justify-between mt-2">
                <div>
                  <Label className="text-slate-700">Conto corrente di agenzia</Label>
                  <div className="text-xs text-slate-500">Se attivo, i Bonifici vanno direttamente sul C/C dell&apos;agenzia e sono esclusi dal versamento.</div>
                </div>
                <Switch
                  data-testid="form-conto-agenzia"
                  checked={!!form.conto_agenzia}
                  onCheckedChange={(v) => setField("conto_agenzia", v)}
                  disabled={form.role === "interno"}
                />
              </div>
              <div className="col-span-2 flex items-center justify-between mt-2">
                <div>
                  <Label className="text-slate-700">Bollettini postali di agenzia</Label>
                  <div className="text-xs text-slate-500">Se attivo, i Bollettini postali del collaboratore vanno direttamente sul conto agenzia e sono esclusi dal versamento.</div>
                </div>
                <Switch
                  data-testid="form-postale-agenzia"
                  checked={!!form.postale_agenzia}
                  onCheckedChange={(v) => setField("postale_agenzia", v)}
                  disabled={form.role === "interno"}
                />
              </div>
              <div className="col-span-2 flex items-center justify-between mt-2">
                <div>
                  <Label className="text-slate-700">Abbuoni abilitati</Label>
                  <div className="text-xs text-slate-500">Se attivo, il collaboratore può inserire un abbuono (sconto) sulle proprie polizze.</div>
                </div>
                <Switch
                  data-testid="form-abbuoni"
                  checked={!!form.abbuoni_abilitati}
                  onCheckedChange={(v) => setField("abbuoni_abilitati", v)}
                  disabled={form.role === "interno"}
                />
              </div>
              <div className="col-span-2 flex items-center justify-between mt-2">
                <div>
                  <Label className="text-slate-700">Flusso di cassa</Label>
                  <div className="text-xs text-slate-500">
                    {agency?.flusso_cassa_collab_modulo
                      ? "Se attivo, il collaboratore vede e tiene aggiornato il proprio saldo liquidità (contante/assegno) giornaliero."
                      : <span className="text-amber-700">Modulo non attivo. Acquistalo dalla pagina Abbonamento per poter abilitare il flusso di cassa sui collaboratori.</span>}
                  </div>
                </div>
                <Switch
                  data-testid="form-flusso-cassa"
                  checked={!!form.flusso_cassa_abilitato}
                  onCheckedChange={(v) => setField("flusso_cassa_abilitato", v)}
                  disabled={form.role === "interno" || !agency?.flusso_cassa_collab_modulo}
                />
              </div>
              <div className="col-span-2 flex items-center justify-between mt-2">
                <div>
                  <Label className="text-slate-700">Gestione sospesi abilitata</Label>
                  <div className="text-xs text-slate-500">
                    {agency?.sospesi_abilitati
                      ? "Se attivo, il collaboratore può inserire polizze come 'Sospeso' (non ancora incassate) e recuperarle dalla pagina Sospesi."
                      : <span className="text-amber-700">Sospesi non abilitati per l&apos;agenzia. Attivali in Impostazioni per poterli assegnare ai collaboratori.</span>}
                  </div>
                </div>
                <Switch
                  data-testid="form-sospesi-enabled"
                  checked={!!form.sospesi_enabled}
                  onCheckedChange={(v) => setField("sospesi_enabled", v)}
                  disabled={form.role === "interno" || !agency?.sospesi_abilitati}
                />
              </div>
              <div className="col-span-2 flex items-center justify-between mt-2">
                <div>
                  <Label className="text-slate-700">Chiudi foglio cassa in autonomia</Label>
                  <div className="text-xs text-slate-500">
                    Se attivo, il foglio cassa di questo collaboratore <strong>non viene chiuso automaticamente</strong> agli orari standard. Sarà il collaboratore a chiuderlo manualmente; in alternativa si chiude alle 23:59.
                  </div>
                </div>
                <Switch
                  data-testid="form-chiusura-autonoma"
                  checked={!!form.chiusura_autonoma}
                  onCheckedChange={(v) => setField("chiusura_autonoma", v)}
                  disabled={form.role === "interno"}
                />
              </div>
              <div className="col-span-2 flex items-center justify-between mt-2">
                <div>
                  <Label className="text-slate-700">Conto separato</Label>
                  <div className="text-xs text-slate-500">
                    Se attivo, il collaboratore gestisce i propri incassi in <strong>due step</strong>: <strong>Step 1</strong> &quot;Versa su CC separato&quot; (auto-convalida, solo liquidi) → <strong>Step 2</strong> &quot;Versa in agenzia&quot; (richiede convalida agente quando vede l&apos;accredito).
                  </div>
                </div>
                <Switch
                  data-testid="form-conto-separato"
                  checked={!!form.conto_separato}
                  onCheckedChange={(v) => setField("conto_separato", v)}
                  disabled={form.role === "interno"}
                />
              </div>
              {form.conto_separato && form.role === "collaboratore" && (
                <div className="col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="direct-methods-section">
                  <Label className="text-slate-700 text-sm">Metodi a versamento diretto su CC agenzia</Label>
                  <div className="text-xs text-slate-500 mb-3">
                    Seleziona i metodi non liquidi che <strong>vanno direttamente sul CC dell&apos;agenzia</strong> (es. POS aziendale, bonifici intestati all&apos;agenzia, bollettini). Le polizze incassate con questi metodi <strong>NON appariranno</strong> nei versamenti del collaboratore (sono già al sicuro sul conto agenzia).
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {["Bancomat", "Bonifico", "Bollettino postale"].map((m) => {
                      const checked = (form.direct_methods || []).includes(m);
                      return (
                        <label key={m} className={`flex items-center gap-2 p-2 rounded-md border cursor-pointer text-sm ${checked ? "border-emerald-700 bg-emerald-50" : "border-slate-200 bg-white"}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            data-testid={`direct-method-${m.replace(/\s+/g, '-').toLowerCase()}`}
                            onChange={(e) => {
                              const cur = form.direct_methods || [];
                              const next = e.target.checked ? [...cur, m] : cur.filter((x) => x !== m);
                              setForm({ ...form, direct_methods: next });
                            }}
                          />
                          <span>{m}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Modalità versamenti PERSONALE del collaboratore */}
              {form.role === "collaboratore" && (
                <div className="col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="form-versamento-mode-section">
                  <Label className="text-slate-700 text-sm">Modalità versamenti in banca</Label>
                  <div className="text-xs text-slate-500 mt-1 mb-3">
                    Scegli come questo collaboratore inserirà i versamenti in banca. <strong>Per polizze</strong>: spunta le singole polizze. <strong>Importo libero</strong>: solo importo totale.
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={form.versamento_mode === "polizze" ? "default" : "outline"}
                      onClick={() => setForm({ ...form, versamento_mode: "polizze" })}
                      data-testid="form-vers-mode-polizze"
                    >
                      Per polizze (con spunte)
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={form.versamento_mode === "importo_libero" ? "default" : "outline"}
                      onClick={() => setForm({ ...form, versamento_mode: "importo_libero" })}
                      data-testid="form-vers-mode-libero"
                    >
                      Importo libero (manuale)
                    </Button>
                  </div>
                </div>
              )}

              {form.role === "collaboratore" && (
                <div className="col-span-2 border-t border-slate-100 pt-4">
                  <Label className="text-slate-700 font-semibold">Modalità invio riepiloghi POS</Label>
                  <div className="text-xs text-slate-500 mt-1 mb-3">
                    Come deve essere strutturato l&apos;allegato di fine giornata di questo collaboratore. <strong>Eredita</strong> usa la configurazione dell&apos;agenzia (Impostazioni). Le altre opzioni sovrascrivono solo per questo utente.
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    {[
                      { v: "", label: "Eredita dall'agenzia", desc: "Usa Impostazioni" },
                      { v: "none", label: "Unico riepilogo", desc: "POS insieme agli altri metodi" },
                      { v: "separato", label: "POS separato", desc: "Due allegati distinti" },
                      { v: "duplicato", label: "POS + copia unica", desc: "Riepilogo completo + solo POS" },
                    ].map((opt) => (
                      <Button
                        key={opt.v || "eredita"}
                        type="button"
                        size="sm"
                        variant={form.pos_split_mode === opt.v ? "default" : "outline"}
                        onClick={() => setForm({ ...form, pos_split_mode: opt.v })}
                        className="w-full h-auto py-2 px-3 flex flex-col items-start justify-start text-left whitespace-normal break-words leading-tight"
                        data-testid={`form-pos-split-${opt.v || "eredita"}`}
                      >
                        <span className="font-semibold text-xs">{opt.label}</span>
                        <span className="text-[10px] opacity-70 mt-0.5">{opt.desc}</span>
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              </>)}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-user-btn">
                Salva
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Nome</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Username</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Email</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">POS</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Bonifici</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Ruolo</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Stato</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={8} className="text-center py-8 text-slate-500">Caricamento…</td></tr>}
              {!loading && users.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-500">Nessun utente.</td></tr>}
              {users.map((u, idx) => (
                <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${idx}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{u.nome} {u.cognome}</td>
                  <td className="px-4 py-3 text-slate-700 font-mono text-xs">{u.username}</td>
                  <td className="px-4 py-3 text-slate-700">{u.email}</td>
                  <td className="px-4 py-3">
                    {u.role === "collaboratore" ? (
                      <Badge variant="outline" className={u.pos_aziendale ? "bg-blue-100 text-blue-800 border-blue-200" : "bg-slate-100 text-slate-700 border-slate-200"}>
                        {u.pos_aziendale ? "Aziendale" : "Personale"}
                      </Badge>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {u.role === "collaboratore" ? (
                      <div className="flex flex-col gap-1">
                        <Badge variant="outline" className={u.conto_agenzia ? "bg-blue-100 text-blue-800 border-blue-200" : "bg-slate-100 text-slate-700 border-slate-200"}>
                          {u.conto_agenzia ? "C/C agenzia" : "C/C personale"}
                        </Badge>
                        <Badge variant="outline" className={u.postale_agenzia ? "bg-blue-100 text-blue-800 border-blue-200" : "bg-slate-100 text-slate-700 border-slate-200"}>
                          {u.postale_agenzia ? "Postale agenzia" : "Postale personale"}
                        </Badge>
                      </div>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={
                      u.role === "agente" ? "bg-slate-900 text-white border-slate-900"
                      : u.role === "interno" ? "bg-purple-100 text-purple-800 border-purple-200"
                      : "bg-slate-100 text-slate-700"
                    }>
                      {u.role === "agente" ? "Agente" : u.role === "interno" ? "Utenza interna" : "Collaboratore"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={u.stato === "attivo" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}>
                      {u.stato === "attivo" ? "Attivo" : "Disattivo"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`edit-user-${idx}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="outline" onClick={() => resetPwd(u)} data-testid={`reset-pwd-${idx}`}><KeyRound className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="outline" onClick={() => toggleStato(u)} data-testid={`toggle-stato-${idx}`}>
                        {u.stato === "attivo" ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                      </Button>
                      {u.role !== "agente" && (
                        <Button size="sm" variant="outline" onClick={() => remove(u)} className="text-red-600 hover:bg-red-50" data-testid={`delete-user-${idx}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
